// ============================================================================
// TABLETOP SERVICE
// All API calls for the Cyber Tabletop Exercise module.
// Types mirror backend/src/types/tabletop.types.ts — kept in sync by hand,
// same convention as reachService.ts / bid service.
// ============================================================================

import { api } from './api';
import type { TabletopSecondScreenState } from '../types/second-screen.types';

export type TabletopExerciseStatus = 'draft' | 'generating' | 'generated' | 'approved' | 'running' | 'complete';
export type TabletopFrameworkPackId = 'csf-2.0' | 'uk-caf';
export type TabletopDeliveryChannel = 'device_voice' | 'browser_tts' | 'text';
export type TabletopPersonaControl = 'human' | 'ai_driven' | 'referenced_only';
export type TabletopPersonaCategory =
  | 'internal_leadership'
  | 'internal_technical'
  | 'external_partner'
  | 'external_oversight'
  | 'threat_actor';

export interface TabletopOrgProfile {
  sector: string;
  sizeDescription?: string;
  thirdPartyDependencies: string[];
  jurisdiction?: string;
  uploadedContextKbRefs?: string[];
}

export interface TabletopThreatProfile {
  actorType: 'ransomware_criminal' | 'hacktivist' | 'state_linked_deniable' | 'insider' | 'opportunistic';
  motivation: string;
  sophistication: 'low' | 'moderate' | 'high';
  ttps: string[];
  attributionNarrative: string;
}

export interface TabletopGenerationState {
  threatModel: 'pending' | 'done';
  personas: 'pending' | 'done';
  scenario: 'pending' | 'done';
  compliance: 'pending' | 'done';
  broadcastAssetsTotal: number;
  broadcastAssetsDone: number;
}

export interface TabletopExercise {
  exerciseId: string;
  projectId: string;
  status: TabletopExerciseStatus;
  seed: string;
  orgProfile: TabletopOrgProfile;
  frameworkPacks: TabletopFrameworkPackId[];
  sessionId?: string;
  currentRunId?: string;
  threatProfile?: TabletopThreatProfile;
  generationState?: TabletopGenerationState;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface TabletopMesEvent {
  id: string;
  simTimeOffsetMs: number;
  label: string;
  detail: string;
}

export interface TabletopMasterScenario {
  exerciseId: string;
  summary: string;
  groundTruthTimeline: TabletopMesEvent[];
  generatedAt: string;
  editedAt?: string;
}

export interface TabletopAct {
  actId: string;
  label: string;
  simTimeStartMs: number;
  simTimeEndMs: number;
  entryInjectIds: string[];
}

export type TabletopInjectStatus = 'true' | 'partial' | 'false';
export type TabletopInjectChannel = 'public' | 'private' | 'broadcast';

export interface TabletopInject {
  exerciseId: string;
  seq: number;
  actId?: string;
  triggerSimTimeMs?: number;
  triggerCondition?: string;
  content: string;
  channel: TabletopInjectChannel;
  recipientPersonaIds: string[];
  status: TabletopInjectStatus;
  isMandatoryReadinessInject?: 'systems_down' | 'unsupported_decision';
  frameworkRefs?: Partial<Record<TabletopFrameworkPackId, string[]>>;
}

export interface TabletopPersonaBehaviourProfile {
  tone?: string;
  errorTendency?: string;
  availability?: string;
  biases?: string[];
}

export interface TabletopPersona {
  exerciseId: string;
  personaId: string;
  displayName: string;
  organisation?: string;
  category: TabletopPersonaCategory;
  control: TabletopPersonaControl;
  assignedParticipantId?: string;
  knowledgeScope: string[];
  knowledgeGaps: string[];
  authority?: string;
  behaviourProfile?: TabletopPersonaBehaviourProfile;
  escalationThresholds?: string[];
  curveballEligible?: boolean;
  deliveryChannel: TabletopDeliveryChannel;
  deviceId?: string;
  ttsVoiceId?: string;
  avatarDefinitionId?: string;
  included?: boolean;
}

// ── Run partition (Phase 3) ─────────────────────────────────────────────────

export type TabletopRunStatus = 'running' | 'held' | 'complete';

export interface TabletopAdjudicationDeadline {
  label: string;
  atSimMs: number;
  consequence: string;
}

export interface TabletopRunMeta {
  runId: string;
  projectId: string;
  exerciseId: string;
  sessionId: string;
  status: TabletopRunStatus;
  startedAt: string;
  compressionRatio: number;
  heldTotalMs: number;
  heldAt?: string;
  currentActId: string;
  fallbackChairActive: boolean;
  ownerTaskId?: string;
  leaseExpiresAt?: string;
  pendingInjectsOnResume?: number;

  // Adjudicator loop (plan 04) — mirrors backend/src/types/tabletop.types.ts
  adjudicationDueAt?: string;
  adjudicationInFlightId?: string;
  pressureLevel?: 0 | 1 | 2 | 3 | 4;
  pressureReason?: string;
  adjudicationDeadlines?: TabletopAdjudicationDeadline[];
  adjudicationSuspended?: boolean;
  adjudicationBudget?: number;
  adjudicationCallsUsed?: number;
}

export interface TabletopSeatState {
  runId: string;
  personaId: string;
  control: TabletopPersonaControl;
  assignedParticipantId?: string;
  deviceId?: string;
  deliveryChannel: TabletopDeliveryChannel;
  muted: boolean;
}

export type TabletopEventKind =
  | 'utterance'
  | 'inject_delivered'
  | 'decision'
  | 'organiser_action'
  | 'clock_transition'
  | 'branch_taken'
  | 'curveball'
  | 'startex'
  | 'endex'
  | 'persona_speaking'
  | 'voice_conversation_ended'
  | 'adjudication';

export interface TabletopRunEvent {
  runId: string;
  eventId: string;
  kind: TabletopEventKind;
  simTimeMs: number;
  wallClockAt: string;
  personaId?: string;
  detail: Record<string, unknown>;
}

export interface TabletopFlag {
  runId: string;
  flagId: string;
  type: 'plan_not_produced' | 'no_owned_decision' | 'missed_statutory_window' | 'no_incident_command' | string;
  detail: string;
  severity: 'low' | 'medium' | 'high';
  simTimeMs: number;
}

// ── Grading (Phase 5) ────────────────────────────────────────────────────────
// Mirrors backend/src/types/tabletop.types.ts's grading additions.

export type TabletopGradingDimensionId =
  | 'plan_adequacy'
  | 'plan_accessibility_under_duress'
  | 'decision_ownership_vs_supplier_deference'
  | 'detection_and_escalation_speed'
  | 'decision_making_quality'
  | 'governance_and_authorisation'
  | 'regulatory_and_legal_compliance'
  | 'cross_boundary_coordination'
  | 'communications'
  | 'political_and_member_management'
  | 'business_continuity_and_service_prioritisation'
  | 'adaptability'
  | 'individual_persona_performance';

export type TabletopBehaviouralRating = 'not_evident' | 'developing' | 'effective' | 'exemplary';

export interface TabletopScorecardEntry {
  dimension: TabletopGradingDimensionId;
  rating: TabletopBehaviouralRating;
  csfRefs: string[];
  evidence: { simTimeMs: number; note: string }[];
}

export interface TabletopGrading {
  exerciseId: string;
  runId: string;
  orgLevel: TabletopScorecardEntry[];
  personaLevel: Record<string, TabletopScorecardEntry[]>;
  recommendations: string[];
  coverageSummary: { functionsEvidenced: string[]; functionsNotEvidenced: string[]; note: string };
  gradedAt: string;
}

export interface TabletopEvidencePack {
  packId: TabletopFrameworkPackId;
  frameworkVersion: string;
  exerciseId: string;
  gradedAt: string;
  entries: { dimension: string; label: string; rating: string; refs: string[]; evidence: TabletopScorecardEntry['evidence'] }[];
  coverage: { evidenced: string[]; notEvidenced: string[]; note: string };
}

export interface TabletopBranchOption {
  optionId: string;
  label: string;
  leadsToActId?: string;
}

export interface TabletopBranch {
  exerciseId: string;
  branchId: string;
  actId: string;
  prompt: string;
  options: TabletopBranchOption[];
}

export interface TabletopCoverageResult {
  exerciseId: string;
  passed: boolean;
  hasSystemsDownInject: boolean;
  hasUnsupportedDecisionInject: boolean;
  checkedItems: { label: string; present: boolean }[];
  checkedAt: string;
}

export interface TabletopFrameworkPack {
  packId: TabletopFrameworkPackId;
  frameworkVersion: string;
  retrievedAt: string;
  dimensions: Record<string, { refs: string[]; label: string }>;
  coverage: { evidenced: string[]; notEvidenced: string[]; note: string };
}

// ── API helpers ────────────────────────────────────────────────────────────

function base(projectId: string) {
  return `/api/projects/${projectId}/tabletop`;
}

// ── Framework packs ──────────────────────────────────────────────────────────

export const tabletopFrameworkPacksAPI = {
  list: (projectId: string) =>
    api.get<{ packs: TabletopFrameworkPack[] }>(`${base(projectId)}/framework-packs`),
};

// ── Exercises ────────────────────────────────────────────────────────────────

export const tabletopExercisesAPI = {
  list: (projectId: string) =>
    api.get<{ exercises: TabletopExercise[] }>(`${base(projectId)}/exercises`),
  get: (projectId: string, exerciseId: string) =>
    api.get<{ exercise: TabletopExercise }>(`${base(projectId)}/exercises/${exerciseId}`),
  create: (projectId: string, data: { seed: string; orgProfile: TabletopOrgProfile; frameworkPacks?: TabletopFrameworkPackId[] }) =>
    api.post<{ exercise: TabletopExercise }>(`${base(projectId)}/exercises`, data),
  update: (projectId: string, exerciseId: string, data: Partial<Pick<TabletopExercise, 'seed' | 'orgProfile' | 'frameworkPacks' | 'sessionId'>>) =>
    api.patch<{ exercise: TabletopExercise }>(`${base(projectId)}/exercises/${exerciseId}`, data),
  remove: (projectId: string, exerciseId: string) =>
    api.delete<{ success: boolean }>(`${base(projectId)}/exercises/${exerciseId}`),
  generate: (projectId: string, exerciseId: string) =>
    api.post<{ exercise: TabletopExercise }>(`${base(projectId)}/exercises/${exerciseId}/generate`),
  approve: (projectId: string, exerciseId: string) =>
    api.post<{ exercise: TabletopExercise }>(`${base(projectId)}/exercises/${exerciseId}/approve`),
  // "Reuse outline": clones an existing exercise's scenario/personas/injects
  // into a new exercise without re-running the AI build pipeline.
  clone: (projectId: string, exerciseId: string) =>
    api.post<{ exercise: TabletopExercise }>(`${base(projectId)}/exercises/${exerciseId}/clone`),
};

// ── MES (organiser hand-authoring) ──────────────────────────────────────────

export const tabletopMesAPI = {
  get: (projectId: string, exerciseId: string) =>
    api.get<{ mes: TabletopMasterScenario | null }>(`${base(projectId)}/exercises/${exerciseId}/mes`),
  put: (projectId: string, exerciseId: string, data: { summary: string; groundTruthTimeline: TabletopMesEvent[] }) =>
    api.put<{ success: boolean }>(`${base(projectId)}/exercises/${exerciseId}/mes`, data),
};

// ── Acts ─────────────────────────────────────────────────────────────────────

export const tabletopActsAPI = {
  list: (projectId: string, exerciseId: string) =>
    api.get<{ acts: TabletopAct[] }>(`${base(projectId)}/exercises/${exerciseId}/acts`),
  put: (projectId: string, exerciseId: string, actId: string, data: Omit<TabletopAct, 'actId'>) =>
    api.put<{ act: TabletopAct }>(`${base(projectId)}/exercises/${exerciseId}/acts/${actId}`, data),
};

// ── Injects ──────────────────────────────────────────────────────────────────

export const tabletopInjectsAPI = {
  list: (projectId: string, exerciseId: string) =>
    api.get<{ injects: TabletopInject[] }>(`${base(projectId)}/exercises/${exerciseId}/injects`),
  put: (projectId: string, exerciseId: string, seq: number, data: Omit<TabletopInject, 'exerciseId' | 'seq'>) =>
    api.put<{ inject: TabletopInject }>(`${base(projectId)}/exercises/${exerciseId}/injects/${seq}`, data),
};

// ── Personas ─────────────────────────────────────────────────────────────────

export const tabletopPersonasAPI = {
  list: (projectId: string, exerciseId: string) =>
    api.get<{ personas: TabletopPersona[] }>(`${base(projectId)}/exercises/${exerciseId}/personas`),
  put: (projectId: string, exerciseId: string, personaId: string, data: Omit<TabletopPersona, 'exerciseId' | 'personaId'>) =>
    api.put<{ persona: TabletopPersona }>(`${base(projectId)}/exercises/${exerciseId}/personas/${personaId}`, data),
  remove: (projectId: string, exerciseId: string, personaId: string) =>
    api.delete<{ success: boolean }>(`${base(projectId)}/exercises/${exerciseId}/personas/${personaId}`),
  promote: (projectId: string, exerciseId: string, personaId: string) =>
    api.post<{ persona: TabletopPersona; alreadyPromoted: boolean }>(`${base(projectId)}/exercises/${exerciseId}/personas/${personaId}/promote`),
};

// ── Branches ─────────────────────────────────────────────────────────────────

export const tabletopBranchesAPI = {
  list: (projectId: string, exerciseId: string) =>
    api.get<{ branches: TabletopBranch[] }>(`${base(projectId)}/exercises/${exerciseId}/branches`),
  put: (projectId: string, exerciseId: string, branchId: string, data: Omit<TabletopBranch, 'exerciseId' | 'branchId'>) =>
    api.put<{ branch: TabletopBranch }>(`${base(projectId)}/exercises/${exerciseId}/branches/${branchId}`, data),
};

// ── Coverage ─────────────────────────────────────────────────────────────────

export const tabletopCoverageAPI = {
  get: (projectId: string, exerciseId: string) =>
    api.get<{ coverage: TabletopCoverageResult | null }>(`${base(projectId)}/exercises/${exerciseId}/coverage`),
};

// ── Run (Phase 3) — STARTEX/ENDEX and live organiser controls ──────────────

export const tabletopRunAPI = {
  start: (projectId: string, exerciseId: string, compressionRatio: number) =>
    api.post<{ run: TabletopRunMeta; exercise: TabletopExercise }>(`${base(projectId)}/exercises/${exerciseId}/run`, { compressionRatio }),
  get: (projectId: string, runId: string) =>
    api.get<{ run: TabletopRunMeta; simTimeMs: number; seats: TabletopSeatState[]; flags: TabletopFlag[] }>(`${base(projectId)}/run/${runId}`),
  events: (projectId: string, runId: string) =>
    api.get<{ events: TabletopRunEvent[] }>(`${base(projectId)}/run/${runId}/events`),
  hold: (projectId: string, runId: string) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/hold`),
  resume: (projectId: string, runId: string) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/resume`),
  setRatio: (projectId: string, runId: string, ratio: number) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/set-ratio`, { ratio }),
  skipToAct: (projectId: string, runId: string, actId: string) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/skip-to-act`, { actId }),
  injectNow: (projectId: string, runId: string, seq: number) =>
    api.post<{ success: boolean }>(`${base(projectId)}/run/${runId}/inject-now`, { seq }),
  curveball: (projectId: string, runId: string, text: string, personaId?: string) =>
    api.post<{ success: boolean }>(`${base(projectId)}/run/${runId}/curveball`, { text, personaId }),
  speak: (projectId: string, runId: string, personaId: string, text: string, openConversation?: boolean) =>
    api.post<{ success: boolean }>(`${base(projectId)}/run/${runId}/speak`, { personaId, text, openConversation }),
  summon: (projectId: string, runId: string, personaId: string, prompt?: string) =>
    api.post<{ success: boolean }>(`${base(projectId)}/run/${runId}/summon`, { personaId, prompt }),
  muteSeat: (projectId: string, runId: string, personaId: string, muted: boolean) =>
    api.post<{ success: boolean }>(`${base(projectId)}/run/${runId}/mute-seat`, { personaId, muted }),
  setSeatDevice: (projectId: string, runId: string, personaId: string, deviceId: string | undefined) =>
    api.post<{ success: boolean }>(`${base(projectId)}/run/${runId}/seat-device`, { personaId, deviceId }),
  emergencyStop: (projectId: string, runId: string) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/emergency-stop`),
  endex: (projectId: string, runId: string) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/endex`),
  suspendAdjudication: (projectId: string, runId: string) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/adjudication/suspend`),
  resumeAdjudication: (projectId: string, runId: string) =>
    api.post<{ run: TabletopRunMeta }>(`${base(projectId)}/run/${runId}/adjudication/resume`),
};

// ── Second screen (Phase 4) ─────────────────────────────────────────────────

export const tabletopSecondScreenAPI = {
  getState: (projectId: string, runId: string) =>
    api.get<{ state: TabletopSecondScreenState | null }>(`${base(projectId)}/run/${runId}/second-screen-state`),
};

// ── Grading (Phase 5) ────────────────────────────────────────────────────────

export const tabletopGradingAPI = {
  get: (projectId: string, exerciseId: string, runId: string) =>
    api.get<{ grading: TabletopGrading | null }>(`${base(projectId)}/exercises/${exerciseId}/grading/${runId}`),
  list: (projectId: string, exerciseId: string) =>
    api.get<{ gradings: TabletopGrading[] }>(`${base(projectId)}/exercises/${exerciseId}/grading`),
  evidencePack: (projectId: string, exerciseId: string, runId: string, packId: TabletopFrameworkPackId) =>
    api.get<{ evidencePack: TabletopEvidencePack }>(`${base(projectId)}/exercises/${exerciseId}/grading/${runId}/evidence-pack`, { params: { pack: packId } }),
};
